import styled from '@emotion/styled';
import { tokens, Icon } from '@hotjar/hotsauce';
import { useState } from 'react';
import { VideoModal } from '../VideoModal';

const Wrapper = styled.div<{ thumbnail: string; backgroundColor: string }>`
  position: relative;
  height: 100%;
  background-color: ${({ backgroundColor }) => backgroundColor};

  :before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background-image: url('${({ thumbnail }) => thumbnail}');
    background-repeat: no-repeat;
    background-position: center center;
    background-size: cover;
    opacity: 0.5;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  background-color: ${tokens.color.primary_cta};
  border-radius: 50%;
  transition: transform 150ms linear;
`;

const PlayLink = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: transparent;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: all 150ms linear;

  :hover {
    cursor: pointer;
    background-color: ${tokens.color.primary_cta}30;

    & ${IconWrapper} {
      transform: scale(1.2);
    }
  }
`;

const StyledIcon = styled(Icon)`
  width: 24px;
  height: 24px;
  left: 2px;
  color: ${tokens.color.text_default_inverse};
`;

const Footer = styled.div<{ withDescription: boolean }>`
  position: absolute;
  display: flex;
  width: 100%;
  bottom: 0;
  with: 100%;
  background-color: ${({ withDescription }) =>
    withDescription ? `${tokens.color.text_light}` : 'transparent'};
  flex-direction: row;
  padding: 6px ${tokens.spacer.x5pt5} 6px ${tokens.spacer.x1pt5};
`;

const Description = styled.span`
  display: block;
  color: ${tokens.color.text_default_inverse};
  ${tokens.text('subtitle2')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Duration = styled.span`
  display: flex;
  position: absolute;
  right: ${tokens.spacer.x1};
  bottom: ${tokens.spacer.x1};
  padding: 0 ${tokens.spacer.x0};
  background-color: ${tokens.BRAND_COLORS.neutral_900};
  border-radius: 2px;
  ${tokens.text('subtitle3')};
  font-weight: 600;
  color: ${tokens.color.text_default_inverse};
`;

export interface VideoThumbnailProps {
  title: string;
  videoUrl: string;
  thumbnail: string;
  backgroundColor?: string;
  onClick?: () => void;
  duration?: string;
  description?: string;
  modalContentLabel: string;
}

export const VideoThumbnail = ({
  title,
  videoUrl,
  thumbnail,
  duration,
  description,
  modalContentLabel,
  backgroundColor = tokens.BRAND_COLORS.noon_100,
  onClick,
}: VideoThumbnailProps): JSX.Element => {
  const [isModalOpened, setIsModalOpened] = useState(false);
  const withDescription = Boolean(description);

  const openModal = (): void => {
    setIsModalOpened(true);
    if (onClick) {
      onClick();
    }
  };

  const closeModal = (): void => {
    setIsModalOpened(false);
  };

  return (
    <>
      <Wrapper backgroundColor={backgroundColor} thumbnail={thumbnail}>
        <PlayLink aria-label="thumbnail" role="button" onClick={openModal}>
          <IconWrapper>
            <StyledIcon icon="play_alt" />
          </IconWrapper>
        </PlayLink>
        <Footer withDescription={withDescription}>
          {withDescription ? (
            <Description data-testid="description">{description}</Description>
          ) : null}
          <Duration>{duration}</Duration>
        </Footer>
      </Wrapper>
      <VideoModal
        contentLabel={modalContentLabel}
        title={title}
        videoUrl={videoUrl}
        isOpen={isModalOpened}
        closeModal={closeModal}
      />
    </>
  );
};
